import { Link } from "@tanstack/react-router";
import {useState, useCallback, useEffect, useRef} from "react";
import {toast} from "react-toastify";
import nacl from "tweetnacl";
import { signIn, getEvmNonce, evmLogin, xummLogin } from '../../lib/api';
import { XummPkce } from  'xumm-oauth2-pkce';
import { useAppKitAccount, useAppKit, useAppKitProvider, useDisconnect } from '@reown/appkit/react'
import {ethers} from "ethers"
import { sessionStorageSet } from '../../lib/window-shim';
import { useI18n } from '../../i18n/context';

function toBase64(uint8Array) {
    return btoa(String.fromCharCode.apply(null, uint8Array));
}

async function loginUser(credentials) {
    const encoder = new TextEncoder();
    const passwordBytes = encoder.encode(credentials.password);
    const hash = nacl.hash(passwordBytes);
    const secureCredentials = {
        ...credentials,
        password: toBase64(hash)
    };
    return signIn(secureCredentials);
}

const SecForm = ({callback}) => {
  const { t } = useI18n();
    const [username, setUserName] = useState('');
    const [password, setPassword] = useState('');
    const [errorMessage, setEM] = useState('')
    const [isLoading, setIsLoading] = useState(false);
    const loginIntentRef = useRef(false);
    const isXrplLoginInProgressRef = useRef(false);
    const isOpeningRef = useRef(false);

    const callbackRef = useRef(callback);
    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    const [xumm] = useState(() => new XummPkce(process.env.REACT_APP_XUMM_API_KEY || 'YOUR_XUMM_API_KEY'));

    const { wallet, open } = useAppKit();
    const { disconnect } = useDisconnect();
    const { walletProvider } = useAppKitProvider("eip155");
    const { isConnected, address, embeddedWalletInfo } = useAppKitAccount();

    useEffect(() => {
      if (isConnected) {
        loginIntentRef.current = true;
      }
    }, [isConnected]);

    const handleApiAuthResponse = useCallback((token) => {
        if (!token?.auth) {
            const errorMsg = token?.message || t("signIn.errorGeneric");
            setEM(errorMsg);

             // Special handling for when a wallet is not yet associated with an account
            if (token?.errorType === "assocevm") {
                toast.info(
                    <><div>{t("signIn.errorWalletNotAssociated")}</div>
                        <Link to="/signup" state={{type: "evm"}} className="pixel-nav-link">
                            {t("signIn.signUpPrompt")}
                        </Link>
                    </>
                );
            } else {
                toast.error(errorMsg);
            }
        } else {
            toast.success(t("signIn.loginSuccess"));
            callbackRef.current(JSON.stringify(token));
        }
    }, [t]);

    const handleXrplAuthResponse = useCallback((token) => {
        if (!token?.auth) {
            const errorMsg = token?.message || t("signIn.errorXumm");
            setEM(errorMsg);
            toast.error(errorMsg);
        } else {
            toast.success(t("signIn.loginSuccess"));
            callbackRef.current(JSON.stringify(token));
        }
    }, [t]);

    const performEvmSignIn = useCallback(async () => {
        if (loginIntentRef.current && isConnected) {
            loginIntentRef.current = false;
            setIsLoading(true);
            setEM('');

            try {
                if (!walletProvider) {
                    throw new Error("Wallet provider is not available after connection.");
                }

                const actualProvider = walletProvider;
                const provider = new ethers.providers.Web3Provider(actualProvider);
                const signer = await provider.getSigner();

                const { nonce } = await getEvmNonce(address);
                if (!nonce) throw new Error("Could not retrieve a sign-in nonce from the server.");

                const messageToSign = `Sign this message to log in with your wallet. ID: ${nonce}`;
                const signature = await signer.signMessage(messageToSign);
                sessionStorageSet("evmSignature", signature);
                sessionStorageSet("evmNonce", nonce);
                const token = await evmLogin(messageToSign, signature);
                handleApiAuthResponse(token);
            } catch (error) {
                const friendlyError = error.reason || error.message || t("signIn.errorUnexpected");
                console.error("EVM Sign-in error:", friendlyError, error);
                setEM(friendlyError);
                toast.error(friendlyError);
            } finally {
                setIsLoading(false);
            }
        }
    }, [isConnected, walletProvider, handleApiAuthResponse, t]);

    const handleEvmSignIn = useCallback(async () => {
        if (isOpeningRef.current) return;
        loginIntentRef.current = true;

        if (isConnected) {
            await performEvmSignIn();
        } else {
            try {
                isOpeningRef.current = true;
                await open();
            } catch (error) {
                console.error("Wallet connection modal error:", error);
                loginIntentRef.current = false;
            } finally {
                isOpeningRef.current = false;
            }
        }
    }, [open, isConnected, performEvmSignIn]);

    const handleXummSuccess = useCallback(async (authorized) => {
        if (isXrplLoginInProgressRef.current) {
            isXrplLoginInProgressRef.current = false;
            const { me, jwt } = authorized;
            try {
                const token = await xummLogin(me.account, jwt);
                handleXrplAuthResponse(token);
            } catch (backendError) {
                console.error("Xumm backend login error:", backendError);
                setEM(backendError.message || t("signIn.errorXummGeneric"));
                toast.error(backendError.message || t("signIn.errorXummGeneric"));
            } finally {
                setIsLoading(false);
            }
        }
    }, [handleXrplAuthResponse, t]);

    const handleXummError = useCallback((error) => {
        console.error('Xumm auth error:', error);
        const errorMessage = error.message || t("signIn.errorXumm");
        setEM(errorMessage);
        toast.error(errorMessage);
        isXrplLoginInProgressRef.current = false;
        setIsLoading(false);
    }, [t]);

    useEffect(() => {
        xumm.on('success', handleXummSuccess);
        xumm.on('error', handleXummError);
        return () => {
            xumm.off('success', handleXummSuccess);
            xumm.off('error', handleXummError);
        };
    }, [xumm, handleXummSuccess, handleXummError]);

    const performEvmSignInRef = useRef(performEvmSignIn);
    useEffect(() => {
      performEvmSignInRef.current = performEvmSignIn;
    }, [performEvmSignIn]);

    useEffect(() => {
      if (isConnected && loginIntentRef.current) {
        performEvmSignInRef.current();
      }
    }, [isConnected]);

    const handlePasswordSignIn = async (e) => {
        e.preventDefault();
        if (!username || !password) {
            setEM(t("signIn.errorRequired"));
            return;
        }
        setIsLoading(true);
        setEM('');

        try {
            const token = await loginUser({
                username,
                password
            });
            handleApiAuthResponse(token);
        } catch (error) {
            console.error("Password Sign-in error:", error);
            const friendlyError = error.message || t("signIn.errorUnexpected");
            setEM(friendlyError);
            toast.error(friendlyError);
        } finally {
            setIsLoading(false);
        }
    }

    const handleXrplSignIn = useCallback(async () => {
        setIsLoading(true);
        setEM('');
        isXrplLoginInProgressRef.current = true;
        try {
            await xumm.authorize();
        } catch (error) {
            console.error("Xumm authorize error:", error);
            isXrplLoginInProgressRef.current = false;
            const friendlyError = error.message || t("signIn.errorXummStart");
            setEM(friendlyError);
            toast.error(friendlyError);
            setIsLoading(false);
        }
    }, [xumm, t]);

    return (
        <div className="col-lg-5 col-md-7 mx-auto mb-30">
            <div className="pixel-card">
                <div className="text-center text-light pt-2">
                    {errorMessage || t("signIn.title")}
                </div>

                <div className="p-3">
                    <form onSubmit={handlePasswordSignIn}>
                        <div className="mb-3">
                            <input type="email" className="pixel-input" placeholder={t("signIn.username")}
                                   value={username}
                                   onChange={e => setUserName(e.target.value)}
                                   disabled={isLoading}
                            />
                        </div>
                        <div className="mb-3">
                            <input type="password" className="pixel-input" placeholder={t("signIn.password")}
                                   value={password}
                                   onChange={e => setPassword(e.target.value)}
                                   disabled={isLoading}
                            />
                        </div>
                        <div className="text-center">
                            <button type="submit"
                                    className="pixel-btn w-100 my-4"
                                    disabled={isLoading}
                            >
                                {isLoading ? t('signIn.signingIn') : t('signIn.submit')}
                            </button>
                        </div>
                    </form>
                    <div className="text-center">
                        {!isConnected ? (
                            <button onClick={handleEvmSignIn}
                                    className="pixel-btn w-100 my-4">
                                {isLoading ? t('signIn.connecting') : t('signIn.connectEVM')}
                            </button>
                        ) : (
                            <>
                                <button onClick={handleEvmSignIn}
                                        className="pixel-btn w-100 my-4">
                                    {isLoading ? t('signIn.processing') : t('signIn.walletSignIn')}
                                </button>
                                <button onClick={disconnect}
                                        className="pixel-btn w-100 my-4" style={{background:'transparent', border:'1px solid rgba(255,255,255,0.12)', color:'#aeb6c8'}}>
                                    {t('nav.disconnectWallet')}
                                </button>
                            </>
                        )}

                        <button onClick={handleXrplSignIn}
                                className="pixel-btn w-100 my-4">
                            {isLoading ? t('signIn.processing') : t('signIn.xummSignIn')}
                        </button>
                    </div>
                    <div style={{display: "flex", justifyContent: "space-between", flexWrap: "wrap"}}>
                        <div className="text-sm mt-2 mb-0">{t("signIn.newHere")}&nbsp;&nbsp;&nbsp;
                            <Link to="/signup"
                                     className="pixel-nav-link">{t("signIn.signUpLink")}</Link>
                        </div>
                        <div className="text-sm mt-2 mb-0">
                            <Link to="/validate" search={{ reset: true }}
                                     className="pixel-nav-link">{t("signIn.forgotPassword")}</Link>
                        </div>

                    </div>
                    <div style={{textAlign: "right"}} className="text-sm mt-4 mb-0">{t("signIn.havingProblems")}&nbsp;&nbsp;&nbsp;
                        <Link to="/contact"
                                 className="pixel-nav-link">{t("signIn.contactUs")}</Link>
                    </div>

                </div>
            </div>
        </div>
    );
}

export default SecForm;
